import SignInContainer from '@ui/sign-in-container/SignInContainer'
import {FormikInput} from '~/src/design-system-shims'
import {Form, Formik} from 'formik'
import {KeyRound, LockKeyhole, Mail, MailCheck} from 'lucide-react'
import React, {useCallback, useState} from 'react'
import {NavLink, useLocation, useNavigate, useParams} from 'react-router-dom'
import * as Yup from 'yup'
import PasswordResetForm from '~/src/components/auth/PasswordResetForm'
import {
  AuthBanner,
  AuthButton,
  AuthUI as S,
  type AuthBannerState,
} from '~/src/components/auth/shell'
import {BASE_ROUTES} from '~/src/routes/constants'
import {
  useConfirmPasswordResetMutation,
  useInitiatePasswordResetMutation,
  useTemporaryPasswordResetMutation,
} from '~/src/store/apis/auth-api'
import {reportCatchError} from '../../utils/errorReporter'
import {
  isIopEnabled,
  getIopBaseUrl,
  getIopClientId,
  getIopRedirectUri,
  getIopPlatformGuid,
} from '~/src/utils/iop-auth'

interface LocationState {
  email?: string
  token?: string // For temporary password reset from email
}

const errorText = {
  sameAsLast3Passwords: 'New password cannot match previous 3 passwords',
}

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{email?: string; confirmationCode?: string}>()
  const locationState = location.state as LocationState | undefined
  const iopEnabled = isIopEnabled()

  // Two scenarios for password reset:
  // 1. Coming from email with tempEmail and token (BASE_ROUTES.PASSWORD_RESET) -> use temporaryPasswordReset
  // 2. User clicks "Forgot Password" from sign-in with email/confirmationCode -> use confirmPasswordReset
  const hasTemporaryToken = !!(locationState?.email && locationState.token)
  const hasConfirmationCode = !!(params.email && params.confirmationCode)
  const directToForgotPassword = hasTemporaryToken || hasConfirmationCode

  const [email, setEmail] = useState(locationState?.email ?? '')
  const [isLoading, setIsLoading] = useState(false)
  const [customMessage, setCustomMessage] = useState('')
  const [showLegacyReset, setShowLegacyReset] = useState(false)

  const handleIopForgotPassword = useCallback(() => {
    const iopParams = new URLSearchParams({
      client_id: getIopClientId(),
      redirect_uri: getIopRedirectUri(),
      platform_guid: getIopPlatformGuid(),
    })
    window.location.href = `${getIopBaseUrl()}/oauth/forgot-password?${iopParams.toString()}`
  }, [])

  // Add countdown timer state (from SHR-FE)
  const [countDown, setCountDown] = useState(60)
  const [activeCounter, setActiveCounter] = useState(false)

  const [initiateReset, {error: initiateError}] = useInitiatePasswordResetMutation()
  const [confirmReset, {error: confirmError}] = useConfirmPasswordResetMutation()
  const [temporaryReset, {error: temporaryError}] = useTemporaryPasswordResetMutation()

  // Add countdown timer effect (from SHR-FE)
  React.useEffect(() => {
    if (activeCounter && countDown > 1) {
      const timer = countDown > 0 && setInterval(() => setCountDown(countDown - 1), 1000)
      return () => clearInterval(timer as NodeJS.Timeout)
    }
    if (activeCounter && countDown === 1) {
      setActiveCounter(false)
      setCountDown(60)
    }
  }, [countDown, activeCounter])

  const emailValidationSchema = Yup.object({
    email: Yup.string().email('Please enter a valid email address').required('Email is required'),
  })

  // SHR-FE handleSubmit for email step
  const handleSubmit = async (values: {email: string}) => {
    setCustomMessage('')
    try {
      setIsLoading(true)
      await initiateReset({email: values.email}).unwrap()
      setEmail(values.email)
      setActiveCounter(true) // Start countdown timer
    } catch (error: unknown) {
      reportCatchError('Failed to initiate password reset:', error)
      const err = error as {data?: {message?: string}}
      // Handle specific error messages like SHR-FE
      if (err.data?.message?.includes('Document not found')) {
        setCustomMessage('Incorrect email')
        setTimeout(() => setCustomMessage(''), 3000)
      } else {
        setCustomMessage('Failed to send reset email. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // SHR-FE handlePasswordResetSubmit for password reset step
  const handlePasswordResetSubmit = async (values: {password: string; confirm: string}) => {
    setCustomMessage('')
    try {
      setIsLoading(true)

      // Scenario 1: Temporary password reset from email link with token
      if (hasTemporaryToken && locationState) {
        await temporaryReset({
          email: locationState.email!,
          token: locationState.token!,
          newPassword: values.password,
        }).unwrap()
      }
      // Scenario 2: Confirm password reset with verification code
      else if (hasConfirmationCode) {
        await confirmReset({
          email: params.email,
          verificationCode: params.confirmationCode,
          newPassword: values.password,
        }).unwrap()
      }

      // Navigate to password confirmation like SHR-FE
      navigate(BASE_ROUTES.PASSWORD_CONFIRMATION, {
        state: {
          password: values.password,
          email: hasTemporaryToken ? locationState.email : params.email,
        },
      })
    } catch (error: unknown) {
      const err = error as {data?: {message?: string}}
      if (err.data?.message?.includes('New password cannot match previous 3 passwords')) {
        setCustomMessage(errorText.sameAsLast3Passwords)
        setTimeout(() => setCustomMessage(''), 5000)
      } else {
        setCustomMessage('Failed to reset password. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToSignIn = () => {
    navigate(BASE_ROUTES.SIGNIN)
  }

  const renderKey = directToForgotPassword
    ? 'reset'
    : iopEnabled && !showLegacyReset
    ? 'single-sign'
    : 'email'

  const emailBanner: AuthBannerState | null = customMessage
    ? {tone: 'error', message: customMessage}
    : initiateError
    ? {tone: 'error', message: 'Failed to send reset email. Please try again.'}
    : null

  const renderEmailStep = () => (
    <Formik
      initialValues={{email}}
      validationSchema={emailValidationSchema}
      onSubmit={handleSubmit}>
      {({isSubmitting}) => (
        <Form>
          <S.Badge aria-hidden="true">{activeCounter ? <MailCheck /> : <Mail />}</S.Badge>
          <S.CardTitle>{activeCounter ? 'Email Sent' : 'Reset Your Password'}</S.CardTitle>
          <S.Lede>
            {activeCounter
              ? `We sent a reset link to ${email}. Check your email and follow the instructions.`
              : 'Enter the login email address associated with your account and we will send you a link to reset your password!'}
          </S.Lede>

          <AuthBanner banner={emailBanner} />

          <S.Fields>
            <FormikInput
              name="email"
              label="Email"
              type="email"
              iconLeft={<Mail aria-hidden="true" />}
              placeholder="name@company.com"
              autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
            />
          </S.Fields>

          <AuthButton
            type="submit"
            loading={isLoading}
            disabled={isSubmitting || (activeCounter && countDown > 0)}>
            {activeCounter ? `Send link to email (${countDown})` : 'Send link to email'}
          </AuthButton>
        </Form>
      )}
    </Formik>
  )

  const renderIopForgotPassword = () => (
    <>
      <S.Badge aria-hidden="true">
        <KeyRound />
      </S.Badge>
      <S.CardTitle>Reset Your Password</S.CardTitle>
      <S.Lede>
        Your account uses Single Sign for authentication. Click below to reset your password through
        Single Sign.
      </S.Lede>
      <AuthButton
        type="button"
        iconLeft={<KeyRound aria-hidden="true" />}
        onClick={handleIopForgotPassword}
        data-testid="iop-forgot-password-button">
        Reset Password via Single Sign
      </AuthButton>
      <S.TextButton
        type="button"
        onClick={() => setShowLegacyReset(true)}
        data-testid="show-legacy-reset">
        Get a reset link by email
      </S.TextButton>
    </>
  )

  const renderContent = () => {
    if (directToForgotPassword) {
      return (
        <>
          <S.Badge aria-hidden="true">
            <LockKeyhole />
          </S.Badge>
          <S.CardTitle>{hasTemporaryToken ? 'Set Password' : 'Reset Password'}</S.CardTitle>
          {hasTemporaryToken && locationState?.email && (
            <S.Lede>Login Email: {locationState.email}</S.Lede>
          )}
          <PasswordResetForm
            isError={!!(confirmError ?? temporaryError)}
            customMessage={customMessage}
            onSubmit={handlePasswordResetSubmit}
          />
        </>
      )
    } else if (iopEnabled && !showLegacyReset) {
      return renderIopForgotPassword()
    } else {
      return renderEmailStep()
    }
  }

  return (
    <SignInContainer
      variant="centered"
      below={
        directToForgotPassword ? null : (
          <S.BelowNote>
            <span>Remember your password?</span>
            <NavLink to={BASE_ROUTES.SIGNIN}>Sign In</NavLink>
          </S.BelowNote>
        )
      }>
      <S.View key={renderKey}>{renderContent()}</S.View>
    </SignInContainer>
  )
}

export default ForgotPassword
